import { FormatBold, FormatItalic, InsertLink } from '@mui/icons-material';
import {
  Box,
  Button,
  IconButton,
  Popover,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useRef, useState } from 'react';
import { bold, isSafeHref, italic, link, safeInlineHtml, TextEdit } from './inlineMarkup';

interface CountedFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  limit: number;
  multiline?: boolean;
  minRows?: number;
  required?: boolean;
  disabled?: boolean;
  /** A problem to show: a client-side check or the backend's refusal text. */
  error?: string;
  help?: string;
  placeholder?: string;
  testId?: string;
}

/** A plain input with a live character count. The count turns red over the limit. */
export function CountedField({
  label,
  value,
  onChange,
  limit,
  multiline,
  minRows,
  required,
  disabled,
  error,
  help,
  placeholder,
  testId,
}: CountedFieldProps) {
  const over = value.length > limit;
  return (
    <TextField
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      multiline={multiline}
      minRows={multiline ? minRows ?? 3 : undefined}
      required={required}
      disabled={disabled}
      placeholder={placeholder}
      error={over || !!error}
      helperText={
        <Box component="span" sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
          <span>{error || help || ' '}</span>
          <span style={{ whiteSpace: 'nowrap' }}>
            {value.length}/{limit}
          </span>
        </Box>
      }
      fullWidth
      size="small"
      inputProps={{ 'data-testid': testId }}
    />
  );
}

interface MarginaliaFieldProps {
  value: string;
  onChange: (value: string) => void;
  limit: number;
  disabled?: boolean;
  error?: string;
}

/**
 * The editor's note: the only field that carries inline formatting. A textarea holding
 * inline HTML (strong, em, links) with a B / I / link toolbar and a live preview.
 */
export function MarginaliaField({ value, onChange, limit, disabled, error }: MarginaliaFieldProps) {
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const [linkAnchor, setLinkAnchor] = useState<HTMLElement | null>(null);
  const [href, setHref] = useState('https://');
  // The selection survives the popover taking focus.
  const selection = useRef({ start: 0, end: 0 });

  const readSelection = () => {
    const el = inputRef.current;
    selection.current = { start: el?.selectionStart ?? value.length, end: el?.selectionEnd ?? value.length };
  };

  const apply = (edit: (v: string, s: number, e: number) => TextEdit) => {
    readSelection();
    const { start, end } = selection.current;
    const next = edit(value, start, end);
    onChange(next.value);
    // Restore the selection after React re-renders the controlled textarea.
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(next.selectionStart, next.selectionEnd);
    });
  };

  const hrefOk = isSafeHref(href);
  const over = value.length > limit;

  return (
    <Box data-testid="marginalia-field">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          Editor's note. The only text that takes bold, italic and links.
        </Typography>
        <Box role="group" aria-label="Formatting" sx={{ display: 'flex' }}>
          <Tooltip title="Bold">
            <span>
              <IconButton size="small" disabled={disabled} onClick={() => apply(bold)} aria-label="Bold">
                <FormatBold fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Italic">
            <span>
              <IconButton size="small" disabled={disabled} onClick={() => apply(italic)} aria-label="Italic">
                <FormatItalic fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Link">
            <span>
              <IconButton
                size="small"
                disabled={disabled}
                aria-label="Link"
                onClick={(e) => {
                  readSelection();
                  setLinkAnchor(e.currentTarget);
                }}
              >
                <InsertLink fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
      <TextField
        value={value}
        onChange={(e) => onChange(e.target.value)}
        multiline
        minRows={3}
        fullWidth
        size="small"
        disabled={disabled}
        inputRef={inputRef}
        error={over || !!error}
        onBlur={readSelection}
        helperText={
          <Box component="span" sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
            <span>{error || ' '}</span>
            <span style={{ whiteSpace: 'nowrap' }}>
              {value.length}/{limit}
            </span>
          </Box>
        }
        inputProps={{ 'data-testid': 'marginalia-input' }}
      />
      {value.trim() && (
        <Box
          sx={{
            mt: 1,
            p: 1.25,
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 1,
            fontSize: 14,
            '& a': { color: 'primary.main' },
          }}
          data-testid="marginalia-preview"
        >
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
            Preview
          </Typography>
          <span dangerouslySetInnerHTML={{ __html: safeInlineHtml(value) }} />
        </Box>
      )}
      <Popover
        open={!!linkAnchor}
        anchorEl={linkAnchor}
        onClose={() => setLinkAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, width: 320 }}>
          <TextField
            label="Link address"
            value={href}
            onChange={(e) => setHref(e.target.value)}
            size="small"
            fullWidth
            autoFocus
            error={!hrefOk && href.trim() !== 'https://'}
            helperText="http, https, mailto, tel or a relative link"
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
            <Button
              size="small"
              variant="contained"
              disabled={!hrefOk || href.trim() === 'https://'}
              onClick={() => {
                const { start, end } = selection.current;
                const next = link(value, start, end, href);
                onChange(next.value);
                setLinkAnchor(null);
                setHref('https://');
              }}
            >
              Add link
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
}
